/**
 * FeedBuilder — admin "Feeds & Logos" tab.
 *
 * Lists streaming feeds (e.g. "NFL Pass 1") and lets an admin create, edit and
 * delete them with a live-rendered badge logo. The backend is the single source of
 * truth for rendering: previews come from /api/admin/feeds/preview, so what you see
 * here is exactly what the dashboard serves.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BACKEND_URL, assetUrl } from '../config';

// Accent colors offered for quick "next pass" creation, cycled per group
const ACCENT_ROTATION = ['#3B82F6', '#F87171', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#EAB308'];

const OVERRIDE_FIELDS = [
  ['bg_top', 'Background top'],
  ['bg_bottom', 'Background bottom'],
  ['border', 'Border'],
  ['tab_start', 'Tab start'],
  ['tab_end', 'Tab end'],
  ['label_color', 'Label text'],
];

/** Turn free text into a feed key: lowercase letters, digits, underscores. */
const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32);

/** SVG markup -> data URL usable in <img> (scripts never run in image context). */
const svgDataUrl = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/** Convert the form's blank strings to nulls so the API derives those values. */
const toPayload = (form) => ({
  key: form.key,
  name: form.name,
  parent_league_key: form.parent_league_key,
  order: Number(form.order) || 1,
  round_filter: form.round_filter === '' || form.round_filter === null ? null : Number(form.round_filter),
  logo: Object.fromEntries(Object.entries(form.logo).map(([k, v]) => [k, v === '' ? null : v])),
});

/** Flatten FastAPI error bodies (string or 422 validation list) into one message. */
const errorMessage = (body, fallback) => {
  if (!body || !body.detail) return fallback;
  if (typeof body.detail === 'string') return body.detail;
  if (Array.isArray(body.detail)) {
    return body.detail
      .map((d) => `${(d.loc || []).filter((p) => !['body', 'feed'].includes(p)).join('.')}: ${d.msg}`)
      .join(' · ');
  }
  return fallback;
};

/** Trigger a browser download for a Blob. */
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Rasterize the badge SVG (168x48 viewBox) to a PNG Blob at the given scale. */
const svgToPngBlob = (svg, scale) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 168 * scale;
      canvas.height = 48 * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG export failed'))), 'image/png');
    };
    img.onerror = () => reject(new Error('Could not load SVG for PNG export'));
    img.src = svgDataUrl(svg);
  });

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500';

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-slate-300 text-xs font-medium mb-1 block">{label}</span>
    {children}
    {hint && <span className="text-slate-500 text-xs mt-1 block">{hint}</span>}
  </label>
);

const FeedBuilder = ({ password, onFeedsChanged }) => {
  const [feeds, setFeeds] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [form, setForm] = useState(null); // null = list view
  const [originalKey, setOriginalKey] = useState(null); // set when editing an existing feed
  const [keyTouched, setKeyTouched] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [preview, setPreview] = useState(null); // { svg, palette }
  const [previewError, setPreviewError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const previewSeq = useRef(0);

  const post = useCallback(
    async (path, body) => {
      const res = await fetch(`${BACKEND_URL}/api/admin/feeds/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, ...body }),
      });
      let data = null;
      try { data = await res.json(); } catch { /* non-JSON error body */ }
      return { ok: res.ok, status: res.status, data };
    },
    [password]
  );

  const loadFeeds = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { ok, data } = await post('list', {});
      if (!ok) throw new Error(errorMessage(data, 'Failed to load feeds'));
      setFeeds(data.feeds || []);
      setLeagues(data.leagues || []);
      setIcons(data.icons || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [post]);

  useEffect(() => { loadFeeds(); }, [loadFeeds]);

  const leagueByKey = useMemo(() => Object.fromEntries(leagues.map((l) => [l.key, l])), [leagues]);

  const feedsByParent = useMemo(() => {
    const groups = {};
    feeds.forEach((f) => { (groups[f.parent_league_key] = groups[f.parent_league_key] || []).push(f); });
    return groups;
  }, [feeds]);

  /** Build a new-feed form for a league, numbered after its existing feeds. */
  const newFormFor = useCallback(
    (leagueKey, template = null) => {
      const league = leagueByKey[leagueKey] || leagues[0];
      if (!league) return null;
      const siblings = feedsByParent[league.key] || [];
      const n = siblings.length + 1;
      const base = template || siblings[siblings.length - 1];
      const name = base ? base.name.replace(/\d+\s*$/, '').trim() + ` ${n}` : `${league.name} Pass ${n}`;
      return {
        key: slugify(base ? base.key.replace(/\d+$/, '') + n : `${league.key}_pass${n}`),
        name,
        parent_league_key: league.key,
        order: n,
        round_filter: base && base.round_filter !== null && base.round_filter !== undefined ? String(base.round_filter) : '',
        logo: {
          wordmark: base ? base.logo.wordmark : league.key.toUpperCase().slice(0, 6),
          label: base ? base.logo.label : 'PASS',
          tab_text: base ? base.logo.tab_text.replace(/\d+\s*$/, '').trim() + ` ${n}` : `FEED ${n}`,
          icon: base ? base.logo.icon : league.default_icon,
          primary: ACCENT_ROTATION[(n - 1) % ACCENT_ROTATION.length],
          bg_top: '', bg_bottom: '', border: '', tab_start: '', tab_end: '', label_color: '',
        },
      };
    },
    [leagueByKey, leagues, feedsByParent]
  );

  const startCreate = (leagueKey = 'nfl') => {
    setOriginalKey(null);
    setKeyTouched(false);
    setShowAdvanced(false);
    setNotice('');
    setError('');
    setForm(newFormFor(leagueKey));
  };

  const startEdit = (feed) => {
    const logo = { ...feed.logo };
    OVERRIDE_FIELDS.forEach(([k]) => { logo[k] = logo[k] || ''; });
    setOriginalKey(feed.key);
    setKeyTouched(true);
    setShowAdvanced(OVERRIDE_FIELDS.some(([k]) => feed.logo[k]));
    setNotice('');
    setError('');
    setForm({
      key: feed.key,
      name: feed.name,
      parent_league_key: feed.parent_league_key,
      order: feed.order,
      round_filter: feed.round_filter === null || feed.round_filter === undefined ? '' : String(feed.round_filter),
      logo,
    });
  };

  const cancel = () => { setForm(null); setPreview(null); setPreviewError(''); setError(''); };

  const setField = (field, value) => setForm((f) => {
    const next = { ...f, [field]: value };
    // Keep the key in sync with the name until the admin edits the key directly
    if (field === 'name' && !keyTouched && !originalKey) next.key = slugify(value);
    return next;
  });
  const setLogo = (field, value) => setForm((f) => ({ ...f, logo: { ...f.logo, [field]: value } }));

  const changeParent = (leagueKey) => {
    if (originalKey) {
      setField('parent_league_key', leagueKey);
      return;
    }
    // For new feeds, re-seed sensible defaults for the chosen league
    setKeyTouched(false);
    setForm(newFormFor(leagueKey));
  };

  // Debounced live preview (latest request wins)
  useEffect(() => {
    if (!form) return undefined;
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      const payload = toPayload(form);
      const { ok, data } = await post('preview', {
        key: /^[a-z0-9_]{2,32}$/.test(payload.key) ? payload.key : null,
        name: payload.name || 'Preview',
        logo: payload.logo,
      }).catch(() => ({ ok: false, data: null }));
      if (seq !== previewSeq.current) return;
      if (ok) { setPreview(data); setPreviewError(''); }
      else setPreviewError(errorMessage(data, 'Preview failed'));
    }, 300);
    return () => clearTimeout(timer);
  }, [form, post]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const { ok, data } = await post('save', { feed: toPayload(form), original_key: originalKey });
      if (!ok) throw new Error(errorMessage(data, 'Save failed'));
      setNotice(`Saved “${data.feed.name}” — now live on the dashboard.`);
      setForm(null);
      setPreview(null);
      await loadFeeds();
      onFeedsChanged && onFeedsChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (key) => {
    setError('');
    const { ok, data } = await post('delete', { key });
    setConfirmDelete(null);
    if (!ok) { setError(errorMessage(data, 'Delete failed')); return; }
    setNotice(`Deleted feed “${key}”. Its games were redistributed to the rest of its group.`);
    if (form && originalKey === key) cancel();
    await loadFeeds();
    onFeedsChanged && onFeedsChanged();
  };

  const downloadSvg = () => preview && downloadBlob(new Blob([preview.svg], { type: 'image/svg+xml' }), `${form.key || 'logo'}.svg`);
  const downloadPng = async (scale) => {
    if (!preview) return;
    try { downloadBlob(await svgToPngBlob(preview.svg, scale), `${form.key || 'logo'}@${scale}x.png`); }
    catch (e) { setError(e.message); }
  };

  if (loading && !feeds.length) return <div className="text-slate-400 text-sm">Loading feeds…</div>;

  // ---------------- Editor view ----------------
  if (form) {
    const league = leagueByKey[form.parent_league_key];
    return (
      <div className="space-y-4" data-testid="feed-editor">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold">{originalKey ? `Edit ${originalKey}` : 'New feed'}</h3>
          <button onClick={cancel} className="text-slate-400 hover:text-white text-sm">← Back to feeds</button>
        </div>
        {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}

        {/* Live preview */}
        <div className="bg-slate-900 rounded-xl border border-slate-700 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <div className="flex flex-col items-center gap-3">
              <div className="bg-slate-800 rounded-lg p-4 w-full flex justify-center">
                {preview ? <img src={svgDataUrl(preview.svg)} alt="Logo preview" className="h-16 w-auto" data-testid="feed-logo-preview" /> : <div className="h-16" />}
              </div>
              <div className="bg-white rounded-lg p-4 w-full flex justify-center">
                {preview && <img src={svgDataUrl(preview.svg)} alt="" className="h-8 w-auto" />}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-slate-400 text-xs">Top: 2× on dark · Bottom: 1× on light. As shown in the league bar:</p>
              <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-slate-800 text-slate-300">
                {preview && <img src={svgDataUrl(preview.svg)} alt="" className="h-5 w-auto" />}
                <span className="font-medium text-sm">{form.name || 'Feed name'}</span>
              </div>
              {previewError && <p className="text-amber-400 text-xs">{previewError}</p>}
              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={downloadSvg} disabled={!preview} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs disabled:opacity-50">Download SVG</button>
                <button onClick={() => downloadPng(2)} disabled={!preview} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs disabled:opacity-50">PNG 2×</button>
                <button onClick={() => downloadPng(4)} disabled={!preview} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs disabled:opacity-50">PNG 4×</button>
              </div>
            </div>
          </div>
        </div>

        {/* Feed settings */}
        <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/50 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Parent league" hint="Games come from this league's schedule">
            <select value={form.parent_league_key} onChange={(e) => changeParent(e.target.value)} className={inputClass} data-testid="feed-parent-select">
              {leagues.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
            </select>
          </Field>
          <Field label="Display name">
            <input value={form.name} maxLength={40} onChange={(e) => setField('name', e.target.value)} className={inputClass} data-testid="feed-name-input" />
          </Field>
          <Field label="Key" hint="Used in URLs — lowercase letters, numbers, underscores">
            <input value={form.key} maxLength={32} onChange={(e) => { setKeyTouched(true); setField('key', slugify(e.target.value)); }} className={`${inputClass} font-mono`} data-testid="feed-key-input" />
          </Field>
          <Field label="Order in group" hint="Feed 1 gets the first game in each kickoff slot">
            <input type="number" min={0} max={999} value={form.order} onChange={(e) => setField('order', e.target.value)} className={inputClass} />
          </Field>
          <Field label="Round filter (optional)" hint={`Prefer games with this round, e.g. 500 = NFL preseason. Blank = all ${league ? league.name : ''} games.`}>
            <input type="number" min={0} max={9999} value={form.round_filter} onChange={(e) => setField('round_filter', e.target.value)} placeholder="All games" className={inputClass} />
          </Field>
        </div>

        {/* Logo design */}
        <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/50 space-y-3">
          <h4 className="text-white text-sm font-semibold">Logo</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Wordmark"><input value={form.logo.wordmark} maxLength={6} onChange={(e) => setLogo('wordmark', e.target.value.toUpperCase())} className={inputClass} data-testid="logo-wordmark-input" /></Field>
            <Field label="Label"><input value={form.logo.label} maxLength={12} onChange={(e) => setLogo('label', e.target.value.toUpperCase())} className={inputClass} /></Field>
            <Field label="Tab text"><input value={form.logo.tab_text} maxLength={10} onChange={(e) => setLogo('tab_text', e.target.value.toUpperCase())} className={inputClass} data-testid="logo-tab-input" /></Field>
            <Field label="Icon">
              <select value={form.logo.icon} onChange={(e) => setLogo('icon', e.target.value)} className={inputClass}>
                {icons.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Primary color">
              <div className="flex items-center gap-2">
                <input type="color" value={form.logo.primary.toLowerCase()} onChange={(e) => setLogo('primary', e.target.value.toUpperCase())} className="h-9 w-12 rounded bg-transparent border border-slate-600 cursor-pointer" data-testid="logo-primary-color" />
                <input value={form.logo.primary} maxLength={7} onChange={(e) => setLogo('primary', e.target.value)} className={`${inputClass} w-28 font-mono`} />
              </div>
            </Field>
            <div className="flex gap-1.5 pb-1">
              {ACCENT_ROTATION.map((c) => (
                <button key={c} type="button" title={c} onClick={() => setLogo('primary', c)} className="w-6 h-6 rounded-full border-2 border-slate-600 hover:border-white" style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <button type="button" onClick={() => setShowAdvanced((s) => !s)} className="text-slate-400 hover:text-white text-xs">
            {showAdvanced ? '▾' : '▸'} Advanced colors (derived from primary unless set)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {OVERRIDE_FIELDS.map(([field, label]) => {
                const derived = preview?.palette?.[field] || '#000000';
                const value = form.logo[field];
                return (
                  <Field key={field} label={label}>
                    <div className="flex items-center gap-2">
                      <input type="color" value={(value || derived).toLowerCase()} onChange={(e) => setLogo(field, e.target.value.toUpperCase())} className="h-8 w-10 rounded bg-transparent border border-slate-600 cursor-pointer" />
                      <span className={`font-mono text-xs ${value ? 'text-white' : 'text-slate-500'}`}>{value || `auto ${derived}`}</span>
                      {value && <button type="button" onClick={() => setLogo(field, '')} className="text-slate-500 hover:text-white text-xs">reset</button>}
                    </div>
                  </Field>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={cancel} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm">Cancel</button>
          <button onClick={save} disabled={saving || !form.key || !form.name} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-50" data-testid="feed-save-btn">
            {saving ? 'Saving…' : originalKey ? 'Save changes' : 'Create feed'}
          </button>
        </div>
      </div>
    );
  }

  // ---------------- List view ----------------
  return (
    <div className="space-y-4" data-testid="feed-list">
      <div className="flex items-center justify-between gap-3">
        <p className="text-slate-400 text-sm">Streaming feeds split a league's games across channels. Each feed gets a generated logo.</p>
        <button onClick={() => startCreate()} className="shrink-0 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium" data-testid="feed-new-btn">+ New feed</button>
      </div>
      {notice && <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm">{notice}</div>}
      {error && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
      {!feeds.length && <div className="text-slate-500 text-sm">No feeds yet.</div>}

      {Object.entries(feedsByParent).map(([parentKey, group]) => (
        <div key={parentKey} className="bg-slate-700/30 rounded-xl border border-slate-600/50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-600/50">
            <span className="text-white text-sm font-semibold">{leagueByKey[parentKey]?.name || parentKey}</span>
            <button onClick={() => { setOriginalKey(null); setKeyTouched(false); setShowAdvanced(false); setNotice(''); setForm(newFormFor(parentKey)); }} className="text-emerald-400 hover:text-emerald-300 text-xs font-medium" data-testid={`feed-add-next-${parentKey}`}>
              + Add {group[0].name.replace(/\d+\s*$/, '').trim()} {group.length + 1}
            </button>
          </div>
          {group.map((feed) => (
            <div key={feed.key} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-700/50 last:border-b-0">
              <img src={assetUrl(feed.logo_url)} alt="" className="h-9 w-auto" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium">{feed.name}</div>
                <div className="text-slate-500 text-xs font-mono">
                  {feed.key} · feed {feed.group_position} of {feed.group_size}
                  {feed.round_filter !== null && feed.round_filter !== undefined ? ` · round ${feed.round_filter}` : ''}
                </div>
              </div>
              {confirmDelete === feed.key ? (
                <div className="flex items-center gap-2">
                  <span className="text-red-400 text-xs">Delete?</span>
                  <button onClick={() => remove(feed.key)} className="px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs" data-testid={`feed-confirm-delete-${feed.key}`}>Yes, delete</button>
                  <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs">No</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(feed)} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs" data-testid={`feed-edit-${feed.key}`}>Edit</button>
                  <button onClick={() => setConfirmDelete(feed.key)} className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-red-500/80 text-slate-300 hover:text-white text-xs" data-testid={`feed-delete-${feed.key}`}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default FeedBuilder;
